// Pure helpers for portfolio deep-link URL params.
// Extracted so they can be unit-tested without mounting PortfolioManager.

// RFC 4122 UUID (v1–v5). Used to reject malformed ?portfolio=… values up front.
export const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const isValidPortfolioId = (
  raw: string | null | undefined,
): raw is string => !!raw && UUID_RE.test(raw);

export const sanitizeQ = (raw: string | null): string | null => {
  if (raw === null) return null;
  // strip control chars; cap length to keep URLs and state sane
  // eslint-disable-next-line no-control-regex
  const cleaned = raw.replace(/[\x00-\x1F\x7F]/g, '').slice(0, 100);
  return cleaned.length > 0 ? cleaned : null;
};

export const sanitizeSide = (raw: string | null): 'buy' | 'sell' | null =>
  raw === 'buy' || raw === 'sell' ? raw : null;

export const sanitizeExpanded = (raw: string | null): boolean =>
  raw === '1' || raw === 'true';

export const sanitizeShare = (raw: string | null): boolean => raw === '1';

// Keys that describe a portfolio deep link. Used when wiping a stale URL.
export const PORTFOLIO_URL_KEYS = [
  'portfolio',
  'q',
  'side',
  'expanded',
  'share',
] as const;

/**
 * Remove every portfolio deep-link key from the given params.
 * Returns a fresh URLSearchParams plus a flag indicating whether anything changed,
 * so callers can avoid a no-op setSearchParams round-trip. Does not mutate `source`.
 */
export const stripPortfolioParams = (
  source: URLSearchParams,
): { next: URLSearchParams; changed: boolean } => {
  const next = new URLSearchParams(source);
  let changed = false;
  PORTFOLIO_URL_KEYS.forEach((k) => {
    if (next.has(k)) {
      next.delete(k);
      changed = true;
    }
  });
  return { next, changed };
};

